package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import kotlinx.html.TagConsumer
import kotlinx.html.a
import kotlinx.html.button
import kotlinx.html.div
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.backend_jobs.JobType
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.CheckboxType
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.component.checkbox
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.routing.PageRouter
import nl.volleyplanner.web.component.backend_jobs.BackendJobManager
import nl.volleyplanner.web.component.backend_jobs.JobFlowComponent
import nl.volleyplanner.web.component.backend_jobs.JobFlowState
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.SwitchingStateFlow
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

class ThuisProgrammaEditorControlsComponent(
    val problemSlug: String,
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager
) : HTMLComponent() {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.controller.thuisprogramma.ThuisProgrammaEditorControlsComponent")
    }

    private val _initialDummyStateFlow = MutableStateFlow<JobFlowState>(JobFlowState(JobType.PlannerJob))
    private val jobTrackerState =
        SwitchingStateFlow<JobFlowState>(CoroutineManager.RENDER_SCOPE, _initialDummyStateFlow)

    val controls: ThuisprogrammaControls
        get() = thuisprogrammaDataManager.thuisprogrammaControlsState.value

    private val linkGeneratorModalComponent = ThuisprogrammaLinkGeneratorComponent(problemSlug)

    init {
        launchAsync(CoroutineManager.RENDER_SCOPE) { BackendJobManager.getAndMonitorLastJob(JobType.PlannerJob) }
            .onComplete { backendJobTracker ->
                if (backendJobTracker != null) {
                    jobTrackerState.updateSource(backendJobTracker.stateFlow)
                }
            }

        CoroutineManager.RENDER_SCOPE.launch {
            jobTrackerState.collect { newState ->
                val oldState = thuisprogrammaDataManager.thuisprogrammaControlsState.value
                if (newState.isRunning() != oldState.plannerRunning) {
                    thuisprogrammaDataManager.thuisprogrammaControlsState.value = thuisprogrammaDataManager.thuisprogrammaControlsState.value.copy(plannerRunning = newState.isRunning(), showLastPlannerResult = true)

                    // Planner has completed, refresh the task data for this version
                    if (!newState.isRunning() && oldState.plannerRunning) {
                        thuisprogrammaDataManager.problemData.getSelectedVersionData().taskDaysState.refresh()
                    }
                }
            }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val versionData = thuisprogrammaDataManager.problemData.getSelectedVersionData()

        return if (AuthContextService.authUser?.authLevel == AuthLevel.AdminUser) {
            val linkGeneratorPromise = componentScope.async {
                createStandaloneComponent(linkGeneratorModalComponent)
            }
            consumer.div {
                component(ListeningComponent(
                    "RunPlannerComponent",
                    listOf(
                        versionData.canPublishVersion,
                        thuisprogrammaDataManager.problemData.getSelectedVersionPropertyState(CoroutineManager.RENDER_SCOPE) { it.taskDaysState.subscribe() }
                    )
                ) {
                    val taskDayResponse = thuisprogrammaDataManager.problemData.getSelectedVersionData().taskDaysState.value
                    val canPublishVersion = versionData.canPublishVersion.value

                    val hasTaskData = taskDayResponse != null
                    val versionSlug = taskDayResponse?.planningProblemVersion?.slug
                    val versionName = taskDayResponse?.planningProblemVersion?.name ?: "Gepubliceerd"
                    div(classes = "row border bg-secondary-subtle rounded p-2 mb-2 d-print-none") {
                        div(classes = "col-auto") {
                                component(
                                    JobFlowComponent(
                                        JobType.PlannerJob,
                                        jobArguments = buildList {
                                            add(WebUserAndOrganisationService.selectedOrganisationState.value!!.organisation.slug)
                                            add(problemSlug)
                                            if (versionSlug != null) add(versionSlug)
                                        },
                                        buttonLabel = "Run planner",
                                        jobStartText = "Geselecteerde versie: <strong>${versionName}</strong><br /><br />" +
                                                "De planner overschrijft alle indelingen die <strong>niet op slot zijn gezet</strong><br />" +
                                                "De planner is ongeveer 5 minuten bezig. Doorgaan?",
                                        enabled = canPublishVersion && hasTaskData
                                    ) { backendJobTracker ->
                                        jobTrackerState.updateSource(backendJobTracker.stateFlow)
                                    }
                                )
                        }
                        div(classes = "col-auto") {
                            button(classes="btn btn-primary") {
                                if (thuisprogrammaDataManager.problemData.selectedProblemVersionSlug != null) {
                                    disabled = true

                                    attributes["data-bs-toggle"] = "tooltip"
                                    attributes["data-bs-title"] = "Alleen mogelijk als de gepubliceerde versie is geselecteerd"
                                }

                                onClickFunction = { event ->
                                    event.preventDefault()
                                    componentScope.launch {
                                        linkGeneratorPromise.await().show()
                                    }
                                }
                                icon("bi-share-fill")
                                +" Link naar thuisprogramma"
                            }
                        }
                        div(classes = "col-auto ms-auto") {
                            checkbox(
                                CheckboxType.Switch,
                                label = "Bewerkmodus",
                                htmlId = "tp-editmode-toggle",
                                inline = true,
                                isChecked = controls.editMode && !controls.plannerRunning && canPublishVersion,
                                isEnabled = !controls.plannerRunning && canPublishVersion,
                                onChangeFunction = { event ->
                                    thuisprogrammaDataManager.thuisprogrammaControlsState.value = thuisprogrammaDataManager.thuisprogrammaControlsState.value.copy(
                                        editMode = (event.currentTarget as HTMLInputElement).checked
                                    )
                                }
                            )
                            checkbox(
                                CheckboxType.Switch,
                                label = "Statistieken tonen",
                                htmlId = "tp-statistics-toggle",
                                inline = true,
                                isChecked = controls.statisticsMode && !controls.plannerRunning,
                                isEnabled = !controls.plannerRunning,
                                onChangeFunction = { event ->
                                    thuisprogrammaDataManager.thuisprogrammaControlsState.value = thuisprogrammaDataManager.thuisprogrammaControlsState.value.copy(
                                        statisticsMode = (event.currentTarget as HTMLInputElement).checked
                                    )
                                }
                            )
                            val problemVersionSlug = PageRouter.currentPageRouteContext.value?.urlState?.queryParameters?.get("version")
                            a {
                                href = "editor/statistieken"
                                if (problemVersionSlug != null) {
                                    href += "?version=$problemVersionSlug"
                                }
                                +"Statistieken per persoon"
                            }
                        }
                    }
                })
                if (thuisprogrammaDataManager.thuisprogrammaControlsState.value.showLastPlannerResult) {
                    component(PlannerJobProgressTrackerComponent(jobTrackerState))
                }
            }
        } else {
            consumer.div {  }
        }
    }

    override suspend fun getState() = listOf(
        thuisprogrammaDataManager.thuisprogrammaControlsState,
        thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState,
        thuisprogrammaDataManager.problemData.problemDataFlow.subscribe()
    ).combineAndLaunch(CoroutineManager.RENDER_SCOPE)
}